import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Image as ImageIcon, Search, Trash2, Copy, Check,
  Upload, RefreshCw, Grid, List, ExternalLink, Film, Download,
  Folder, FolderPlus, ChevronRight, ArrowUp, FolderInput, X,
  UserPlus, Users, Mail, Calendar, Eye, EyeOff, Pencil, CheckSquare, Square,
  ArrowUpDown, ArrowDownAZ, ArrowUpAZ, Clock, Share2, Link as LinkIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { API, getAuthHeaders } from '../App';

export default function AdminContent() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  // Tab state
  const [activeTab, setActiveTab] = useState('creators'); // 'creators' | 'gallery'
  
  // ============ CONTENT CREATORS STATE ============
  const [creators, setCreators] = useState([]);
  const [creatorsLoading, setCreatorsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCreator, setEditingCreator] = useState(null);
  const [deleteCreatorId, setDeleteCreatorId] = useState(null);
  const [copiedCreatorId, setCopiedCreatorId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [creatorForm, setCreatorForm] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  
  const [editForm, setEditForm] = useState({
    full_name: '',
    password: '',
    is_active: true
  });

  // ============ GALLERY STATE ============
  const [folders, setFolders] = useState([]);
  const [media, setMedia] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteMediaId, setDeleteMediaId] = useState(null);
  const [copiedMediaId, setCopiedMediaId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  
  // ============ SELECTION STATE ============
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [allFolders, setAllFolders] = useState([]);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [creatingShareLink, setCreatingShareLink] = useState(false);
  const [downloadingSelected, setDownloadingSelected] = useState(false);

  // ============ CONTENT CREATORS LOGIC ============
  const fetchCreators = useCallback(async () => {
    try {
      setCreatorsLoading(true);
      const response = await axios.get(`${API}/media/content-creators`, {
        headers: getAuthHeaders()
      });
      setCreators(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки');
    } finally {
      setCreatorsLoading(false);
    }
  }, [getAuthHeaders]);

  const handleCreateCreator = async (e) => {
    e.preventDefault();
    if (!creatorForm.email || !creatorForm.password || !creatorForm.full_name) {
      toast.error('Заполните все поля');
      return;
    }
    
    try {
      await axios.post(`${API}/media/content-creators`, creatorForm, {
        headers: getAuthHeaders()
      });
      toast.success('Контент-мейкер создан');
      setShowAddDialog(false);
      setCreatorForm({ email: '', password: '', full_name: '' });
      fetchCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания');
    }
  };

  const handleDeleteCreator = async () => {
    if (!deleteCreatorId) return;
    try {
      await axios.delete(`${API}/media/content-creators/${deleteCreatorId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Контент-мейкер удалён');
      setDeleteCreatorId(null);
      fetchCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const openEditDialog = (creator) => {
    setEditingCreator(creator);
    setEditForm({
      full_name: creator.full_name || '',
      password: '',
      is_active: creator.is_active !== false
    });
    setShowEditDialog(true);
  };

  const handleEditCreator = async (e) => {
    e.preventDefault();
    if (!editingCreator) return;
    
    try {
      const updateData = { full_name: editForm.full_name, is_active: editForm.is_active };
      if (editForm.password) updateData.password = editForm.password;
      
      await axios.put(`${API}/media/content-creators/${editingCreator.id}`, updateData, {
        headers: getAuthHeaders()
      });
      toast.success('Контент-мейкер обновлён');
      setShowEditDialog(false);
      setEditingCreator(null);
      fetchCreators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const generatePassword = (forEdit = false) => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    if (forEdit) {
      setEditForm({ ...editForm, password });
    } else {
      setCreatorForm({ ...creatorForm, password });
    }
  };

  const copyToClipboard = (text, id, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'creator') {
      setCopiedCreatorId(id);
      setTimeout(() => setCopiedCreatorId(null), 2000);
    } else {
      setCopiedMediaId(id);
      setTimeout(() => setCopiedMediaId(null), 2000);
    }
  };

  // ============ GALLERY LOGIC ============
  const fetchGalleryContent = useCallback(async () => {
    setGalleryLoading(true);
    try {
      const foldersRes = await axios.get(`${API}/media/folders`, {
        params: { parent_id: currentFolder },
        headers: getAuthHeaders()
      });
      setFolders(foldersRes.data);

      const params = new URLSearchParams({ page, limit: 50, sort_by: sortBy });
      if (search) params.append('search', search);
      // Always pass folder_id - empty string means root (files without folder)
      params.append('folder_id', currentFolder || '');
      
      const response = await axios.get(`${API}/media/gallery?${params}`, {
        headers: getAuthHeaders()
      });
      
      setMedia(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Ошибка загрузки галереи');
    } finally {
      setGalleryLoading(false);
    }
  }, [page, search, currentFolder, sortBy, getAuthHeaders]);

  const openFolder = (folder) => {
    setCurrentFolder(folder.id);
    setBreadcrumbs([...breadcrumbs, folder]);
    setPage(1);
  };

  const goToRoot = () => {
    setCurrentFolder(null);
    setBreadcrumbs([]);
    setPage(1);
  };

  const goToBreadcrumb = (index) => {
    if (index === -1) {
      goToRoot();
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);
      setCurrentFolder(newBreadcrumbs[newBreadcrumbs.length - 1].id);
      setPage(1);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(`${API}/media/folders`, {
        name: newFolderName,
        parent_id: currentFolder
      }, { headers: getAuthHeaders() });
      toast.success('Папка создана');
      setShowNewFolder(false);
      setNewFolderName('');
      fetchGalleryContent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания папки');
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const totalFiles = files.length;
    let uploaded = 0;
    let failed = 0;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress({ current: i + 1, total: totalFiles, fileName: file.name });
      
      const formData = new FormData();
      formData.append('file', file);
      if (currentFolder) formData.append('folder_id', currentFolder);
      
      try {
        await axios.post(`${API}/media/upload`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
        uploaded++;
      } catch (error) {
        failed++;
        console.error(`Upload failed: ${file.name}`, error);
      }
    }
    
    setUploading(false);
    setUploadProgress({ current: 0, total: 0, fileName: '' });
    
    if (uploaded > 0) {
      toast.success(`Загружено ${uploaded} из ${totalFiles} файлов`);
      fetchGalleryContent();
    }
    if (failed > 0 && uploaded === 0) {
      toast.error(`Ошибка загрузки ${failed} файлов`);
    }
    e.target.value = '';
  };

  const handleDeleteMedia = async () => {
    if (!deleteMediaId) return;
    try {
      await axios.delete(`${API}/media/gallery/${deleteMediaId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Файл удалён');
      setDeleteMediaId(null);
      fetchGalleryContent();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  // ============ SELECTION & BULK OPERATIONS ============
  const toggleFileSelection = (fileId) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
    if (newSelected.size === 0) {
      setSelectionMode(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedFiles.size === media.length) {
      setSelectedFiles(new Set());
      setSelectionMode(false);
    } else {
      setSelectedFiles(new Set(media.map(m => m.id)));
    }
  };

  const cancelSelection = () => {
    setSelectedFiles(new Set());
    setSelectionMode(false);
  };

  const fetchAllFolders = async () => {
    try {
      const response = await axios.get(`${API}/media/folders/all`, {
        headers: getAuthHeaders()
      });
      setAllFolders(response.data);
    } catch (error) {
      // Fallback: use current folders
      setAllFolders(folders);
    }
  };

  const openMoveDialog = async () => {
    await fetchAllFolders();
    setShowMoveDialog(true);
  };

  const handleBulkMove = async (targetFolderId) => {
    const fileIds = Array.from(selectedFiles);
    let moved = 0;
    
    for (const fileId of fileIds) {
      try {
        await axios.put(`${API}/media/files/${fileId}/move`, 
          { folder_id: targetFolderId },
          { headers: getAuthHeaders() }
        );
        moved++;
      } catch (error) {
        console.error(`Failed to move ${fileId}:`, error);
      }
    }
    
    if (moved > 0) {
      toast.success(`Перемещено ${moved} файлов`);
      setSelectedFiles(new Set());
      setSelectionMode(false);
      setShowMoveDialog(false);
      fetchGalleryContent();
    } else {
      toast.error('Ошибка перемещения');
    }
  };

  const handleBulkDelete = async () => {
    const fileIds = Array.from(selectedFiles);
    let deleted = 0;
    
    for (const fileId of fileIds) {
      try {
        await axios.delete(`${API}/media/gallery/${fileId}`, {
          headers: getAuthHeaders()
        });
        deleted++;
      } catch (error) {
        console.error(`Failed to delete ${fileId}:`, error);
      }
    }
    
    if (deleted > 0) {
      toast.success(`Удалено ${deleted} файлов`);
      setSelectedFiles(new Set());
      setSelectionMode(false);
      setShowBulkDelete(false);
      fetchGalleryContent();
    } else {
      toast.error('Ошибка удаления');
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || file.name || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      window.open(file.url, '_blank');
    }
  };

  // Download selected files
  const handleDownloadSelected = async () => {
    if (selectedFiles.size === 0) return;
    
    setDownloadingSelected(true);
    const filesToDownload = media.filter(m => selectedFiles.has(m.id));
    
    for (const file of filesToDownload) {
      await handleDownload(file);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    setDownloadingSelected(false);
    toast.success(`Скачано ${filesToDownload.length} файлов`);
  };

  // Create share link for selected files
  const handleCreateShareLink = async () => {
    if (selectedFiles.size === 0) return;
    
    setCreatingShareLink(true);
    try {
      const response = await axios.post(`${API}/media/share`, {
        file_ids: Array.from(selectedFiles)
      }, { headers: getAuthHeaders() });
      
      const fullLink = `${window.location.origin}/share/${response.data.token}`;
      setShareLink(fullLink);
      setShowShareLink(true);
      toast.success('Ссылка создана!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания ссылки');
    } finally {
      setCreatingShareLink(false);
    }
  };

  // Copy share link
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Ссылка скопирована!');
  };

  // Fetch data on tab change
  useEffect(() => {
    if (activeTab === 'creators') {
      fetchCreators();
    } else {
      fetchGalleryContent();
    }
  }, [activeTab, fetchCreators, fetchGalleryContent]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <ImageIcon className="w-6 h-6 text-emerald-600" />
                <div>
                  <h1 className="text-xl font-bold">Контент</h1>
                  <p className="text-sm text-gray-500">Управление медиа и контент-мейкерами</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('creators')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'creators'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Контент-мейкеры ({creators.length})
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'gallery'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <ImageIcon className="w-4 h-4 inline mr-2" />
              Галерея ({totalItems})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* ============ CREATORS TAB ============ */}
        {activeTab === 'creators' && (
          <div>
            {/* Info + Add Button */}
            <div className="flex items-center justify-between mb-6">
              <Card className="flex-1 mr-4 bg-emerald-50 border-emerald-200">
                <CardContent className="py-3 px-4">
                  <p className="text-sm text-emerald-800">
                    <strong>Media Studio URL:</strong>{' '}
                    <code className="bg-emerald-100 px-2 py-0.5 rounded text-xs">
                      {window.location.origin}/{currentLang}/media-studio
                    </code>
                    <button
                      onClick={() => copyToClipboard(`${window.location.origin}/${currentLang}/media-studio`, 'url', 'creator')}
                      className="ml-2 text-emerald-600 hover:text-emerald-700"
                    >
                      {copiedCreatorId === 'url' ? <Check className="w-4 h-4 inline" /> : <Copy className="w-4 h-4 inline" />}
                    </button>
                  </p>
                </CardContent>
              </Card>
              <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Добавить
              </Button>
            </div>

            {/* Creators List */}
            {creatorsLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : creators.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Нет контент-мейкеров</h3>
                  <p className="text-gray-500 mb-4">Добавьте первого контент-мейкера</p>
                  <Button onClick={() => setShowAddDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Добавить
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {creators.map((creator) => (
                  <Card key={creator.id}>
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <span className="text-emerald-600 font-bold">
                              {creator.full_name?.charAt(0)?.toUpperCase() || 'C'}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-medium">{creator.full_name}</h3>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Mail className="w-3 h-3" />
                              {creator.email}
                              <button
                                onClick={() => copyToClipboard(creator.email, creator.id, 'creator')}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                {copiedCreatorId === creator.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEditDialog(creator)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteCreatorId(creator.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============ GALLERY TAB ============ */}
        {activeTab === 'gallery' && (
          <div>
            {/* Selection Toolbar - appears when files are selected */}
            {selectionMode && selectedFiles.size > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button onClick={cancelSelection} className="text-gray-500 hover:text-gray-700">
                    <X className="w-5 h-5" />
                  </button>
                  <span className="font-medium text-emerald-800">
                    Выбрано: {selectedFiles.size} {selectedFiles.size === 1 ? 'файл' : selectedFiles.size < 5 ? 'файла' : 'файлов'}
                  </span>
                  <button
                    onClick={toggleSelectAll}
                    className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                  >
                    {selectedFiles.size === media.length ? 'Снять всё' : 'Выбрать все'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDownloadSelected}
                    disabled={downloadingSelected}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloadingSelected ? 'Скачивание...' : 'Скачать'}
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleCreateShareLink}
                    disabled={creatingShareLink}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    {creatingShareLink ? 'Создание...' : 'Создать ссылку'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={openMoveDialog}>
                    <FolderInput className="w-4 h-4 mr-2" />
                    Переместить
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setShowBulkDelete(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Удалить
                  </Button>
                </div>
              </div>
            )}

            {/* Gallery Toolbar */}
            <div className="flex items-center justify-between mb-4">
              {/* Breadcrumbs */}
              <div className="flex items-center gap-2">
                <button 
                  onClick={goToRoot}
                  className={`text-sm ${currentFolder === null ? 'text-emerald-600 font-medium' : 'text-gray-600 hover:text-emerald-600'}`}
                >
                  Все файлы
                </button>
                {breadcrumbs.map((folder, index) => (
                  <React.Fragment key={folder.id}>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <button
                      onClick={() => goToBreadcrumb(index)}
                      className={`text-sm ${index === breadcrumbs.length - 1 ? 'text-emerald-600 font-medium' : 'text-gray-600 hover:text-emerald-600'}`}
                    >
                      {folder.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {media.length > 0 && !selectionMode && (
                  <Button variant="outline" size="sm" onClick={() => setSelectionMode(true)}>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    Выбрать
                  </Button>
                )}
                {/* Sort Dropdown */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-9 px-3 text-sm border rounded-md bg-white hover:bg-gray-50 cursor-pointer"
                >
                  <option value="date_desc">Новые сначала</option>
                  <option value="date_asc">Старые сначала</option>
                  <option value="name_asc">По имени А-Я</option>
                  <option value="name_desc">По имени Я-А</option>
                </select>
                <Input
                  placeholder="Поиск..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-40"
                />
                <Button variant="outline" size="icon" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
                  {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowNewFolder(true)}>
                  <FolderPlus className="w-4 h-4 mr-1" />
                  Папка
                </Button>
                <label className="cursor-pointer">
                  <input type="file" multiple accept="image/*,video/*" onChange={handleUpload} className="hidden" disabled={uploading} />
                  <Button asChild className={`${uploading ? 'bg-emerald-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} disabled={uploading}>
                    <span>
                      {uploading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          {uploadProgress.current}/{uploadProgress.total}
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          Загрузить
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Upload Progress Banner */}
            {uploading && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-5 h-5 text-emerald-600 animate-spin" />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-emerald-800">
                        Загрузка файла {uploadProgress.current} из {uploadProgress.total}
                      </span>
                      <span className="text-emerald-600">
                        {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                      </span>
                    </div>
                    <div className="w-full bg-emerald-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-emerald-700 mt-1 truncate">
                      {uploadProgress.fileName}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {galleryLoading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : (
              <>
                {currentFolder && (
                  <button
                    onClick={() => breadcrumbs.length > 1 ? goToBreadcrumb(breadcrumbs.length - 2) : goToRoot()}
                    className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 mb-4"
                  >
                    <ArrowUp className="w-4 h-4" />
                    Назад
                  </button>
                )}

                {/* Folders */}
                {folders.length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-sm font-medium text-gray-500 mb-3">Папки</h2>
                    <div className={viewMode === 'grid' ? "grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" : "space-y-2"}>
                      {folders.map((folder) => (
                        <div
                          key={folder.id}
                          onClick={() => openFolder(folder)}
                          className={viewMode === 'grid'
                            ? "bg-white rounded-lg border p-3 hover:shadow-md cursor-pointer text-center"
                            : "bg-white rounded-lg border p-3 flex items-center gap-3 hover:shadow-md cursor-pointer"
                          }
                        >
                          <Folder className={viewMode === 'grid' ? "w-10 h-10 text-amber-500 mx-auto mb-2" : "w-6 h-6 text-amber-500"} />
                          <span className="text-sm font-medium truncate">{folder.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Files */}
                {media.length > 0 ? (
                  <div>
                    <h2 className="text-sm font-medium text-gray-500 mb-3">Файлы ({totalItems})</h2>
                    <div className={viewMode === 'grid' ? "grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" : "space-y-2"}>
                      {media.map((item) => (
                        <div
                          key={item.id}
                          className={`${viewMode === 'grid'
                            ? "bg-white rounded-lg border overflow-hidden hover:shadow-md group relative"
                            : "bg-white rounded-lg border p-3 flex items-center justify-between hover:shadow-md"
                          } ${selectedFiles.has(item.id) ? 'ring-2 ring-emerald-500' : ''}`}
                        >
                          {viewMode === 'grid' ? (
                            <>
                              {/* Selection checkbox */}
                              {selectionMode && (
                                <button
                                  onClick={() => toggleFileSelection(item.id)}
                                  className="absolute top-2 left-2 z-10 p-1 bg-white rounded shadow"
                                >
                                  {selectedFiles.has(item.id) ? (
                                    <CheckSquare className="w-5 h-5 text-emerald-600" />
                                  ) : (
                                    <Square className="w-5 h-5 text-gray-400" />
                                  )}
                                </button>
                              )}
                              <div 
                                className="aspect-square bg-gray-100 cursor-pointer" 
                                onClick={() => selectionMode ? toggleFileSelection(item.id) : setSelectedImage(item)}
                              >
                                {item.file_type === 'video' ? (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Film className="w-8 h-8 text-gray-400" />
                                  </div>
                                ) : (
                                  <img src={item.url} alt={item.name} className="w-full h-full object-cover" />
                                )}
                              </div>
                              {!selectionMode && (
                                <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-1">
                                  <button onClick={() => handleDownload(item)} className="p-1.5 bg-emerald-500 text-white rounded-full shadow hover:bg-emerald-600">
                                    <Download className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => copyToClipboard(item.url, item.id, 'media')} className="p-1.5 bg-white rounded-full shadow hover:bg-gray-100">
                                    {copiedMediaId === item.id ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                                  </button>
                                  <button onClick={() => setDeleteMediaId(item.id)} className="p-1.5 bg-red-500 text-white rounded-full shadow hover:bg-red-600">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                              <div className="p-2">
                                <p className="text-xs font-medium truncate">{item.name}</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-3">
                                {selectionMode && (
                                  <button onClick={() => toggleFileSelection(item.id)}>
                                    {selectedFiles.has(item.id) ? (
                                      <CheckSquare className="w-5 h-5 text-emerald-600" />
                                    ) : (
                                      <Square className="w-5 h-5 text-gray-400" />
                                    )}
                                  </button>
                                )}
                                {item.file_type === 'video' ? (
                                  <Film className="w-10 h-10 text-gray-400" />
                                ) : (
                                  <img src={item.url} alt={item.name} className="w-10 h-10 object-cover rounded" />
                                )}
                                <div>
                                  <p className="text-sm font-medium">{item.name}</p>
                                  <p className="text-xs text-gray-500">{item.uploaded_by_name}</p>
                                </div>
                              </div>
                              {!selectionMode && (
                                <div className="flex items-center gap-2">
                                  <Button variant="ghost" size="icon" onClick={() => handleDownload(item)}><Download className="w-4 h-4 text-emerald-600" /></Button>
                                  <Button variant="ghost" size="icon" onClick={() => copyToClipboard(item.url, item.id, 'media')}>
                                    {copiedMediaId === item.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                  </Button>
                                  <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleteMediaId(item.id)}><Trash2 className="w-4 h-4" /></Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex justify-center gap-2 mt-6">
                        <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Назад</Button>
                        <span className="py-2 px-4 text-sm">{page} из {totalPages}</span>
                        <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Далее</Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{currentFolder ? 'Папка пуста' : 'Галерея пуста'}</h3>
                    <p className="text-gray-500">Загрузите файлы</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* ============ DIALOGS ============ */}

      {/* Add Creator Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новый контент-мейкер</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCreator} className="space-y-4">
            <div>
              <Label>Имя</Label>
              <Input placeholder="Имя" value={creatorForm.full_name} onChange={(e) => setCreatorForm({ ...creatorForm, full_name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" placeholder="email@example.com" value={creatorForm.email} onChange={(e) => setCreatorForm({ ...creatorForm, email: e.target.value })} />
            </div>
            <div>
              <Label>Пароль</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Пароль"
                    value={creatorForm.password}
                    onChange={(e) => setCreatorForm({ ...creatorForm, password: e.target.value })}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="button" variant="outline" onClick={() => generatePassword(false)}>Сгенерировать</Button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Отмена</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Создать</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Creator Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактировать контент-мейкера</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditCreator} className="space-y-4">
            <div>
              <Label>Имя</Label>
              <Input placeholder="Имя" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} />
            </div>
            <div>
              <Label>Новый пароль (оставьте пустым, чтобы не менять)</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Новый пароль"
                    value={editForm.password}
                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button type="button" variant="outline" onClick={() => generatePassword(true)}>Сгенерировать</Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} className="w-4 h-4 text-emerald-600 rounded" />
              <Label htmlFor="is_active" className="cursor-pointer">Активен</Label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Отмена</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Сохранить</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Creator Confirmation */}
      <AlertDialog open={!!deleteCreatorId} onOpenChange={() => setDeleteCreatorId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить контент-мейкера?</AlertDialogTitle>
            <AlertDialogDescription>Аккаунт будет удалён. Загруженные файлы останутся.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCreator} className="bg-red-500 hover:bg-red-600">Удалить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новая папка</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Название папки" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewFolder(false)}>Отмена</Button>
              <Button onClick={handleCreateFolder} className="bg-emerald-600 hover:bg-emerald-700">Создать</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedImage?.name}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4">
              {selectedImage.file_type === 'video' ? (
                <video src={selectedImage.url} controls className="w-full rounded" />
              ) : (
                <img src={selectedImage.url} alt={selectedImage.name} className="w-full rounded" />
              )}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">Загрузил: {selectedImage.uploaded_by_name}</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleDownload(selectedImage)}><Download className="w-4 h-4 mr-2" />Скачать</Button>
                  <Button variant="outline" onClick={() => copyToClipboard(selectedImage.url, selectedImage.id, 'media')}>
                    {copiedMediaId === selectedImage.id ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}URL
                  </Button>
                  <Button variant="outline" asChild>
                    <a href={selectedImage.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4 mr-2" />Открыть</a>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Media Confirmation */}
      <AlertDialog open={!!deleteMediaId} onOpenChange={() => setDeleteMediaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">⚠️ Удалить файл навсегда?</AlertDialogTitle>
            <AlertDialogDescription>
              Файл будет <strong>безвозвратно удалён</strong> с сервера Cloudflare. 
              Это действие нельзя отменить. Файл исчезнет из всех мест, где он использовался.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMedia} className="bg-red-500 hover:bg-red-600">
              Удалить навсегда
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Files Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Переместить {selectedFiles.size} {selectedFiles.size === 1 ? 'файл' : 'файлов'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {/* Move to root */}
            <button
              onClick={() => handleBulkMove(null)}
              className={`w-full text-left p-3 rounded-lg border hover:bg-gray-50 flex items-center gap-3 ${currentFolder === null ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={currentFolder === null}
            >
              <Folder className="w-5 h-5 text-gray-400" />
              <span>Корневая папка</span>
            </button>
            
            {/* All folders */}
            {allFolders.length > 0 ? (
              allFolders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => handleBulkMove(folder.id)}
                  className={`w-full text-left p-3 rounded-lg border hover:bg-gray-50 flex items-center gap-3 ${currentFolder === folder.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={currentFolder === folder.id}
                >
                  <Folder className="w-5 h-5 text-amber-500" />
                  <span>{folder.name}</span>
                  {currentFolder === folder.id && <span className="text-xs text-gray-400">(текущая)</span>}
                </button>
              ))
            ) : (
              <p className="text-center text-gray-500 py-4">Нет папок. Создайте папку сначала.</p>
            )}
          </div>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowMoveDialog(false)}>Отмена</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">⚠️ Удалить {selectedFiles.size} {selectedFiles.size === 1 ? 'файл' : 'файлов'} навсегда?</AlertDialogTitle>
            <AlertDialogDescription>
              Выбранные файлы будут <strong>безвозвратно удалены</strong> с сервера Cloudflare. 
              Это действие нельзя отменить. Файлы исчезнут из всех мест, где они использовались.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-500 hover:bg-red-600">
              Удалить навсегда ({selectedFiles.size})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Link Dialog */}
      <Dialog open={showShareLink} onOpenChange={setShowShareLink}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-emerald-600" />
              Ссылка для скачивания
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Отправьте эту ссылку — получатель сможет просмотреть и скачать {selectedFiles.size} файл(ов).
            </p>
            <div className="flex gap-2">
              <Input 
                value={shareLink} 
                readOnly 
                className="flex-1 bg-gray-50"
              />
              <Button onClick={copyShareLink} className="bg-emerald-600 hover:bg-emerald-700">
                <Copy className="w-4 h-4 mr-2" />
                Копировать
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowShareLink(false)}>
                Закрыть
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.open(shareLink, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Открыть
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
